/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/


// @mui icons

import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import ContactEmergencyRoundedIcon from "@mui/icons-material/ContactEmergencyRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DocumentScannerRoundedIcon from "@mui/icons-material/DocumentScannerRounded";
import MedicationIcon from "@mui/icons-material/Medication";
import BloodtypeIcon from "@mui/icons-material/Bloodtype";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import ContactsRoundedIcon from "@mui/icons-material/ContactsRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import PermContactCalendarRoundedIcon from "@mui/icons-material/PermContactCalendarRounded";
import CurrencyExchange from "@mui/icons-material/CurrencyExchange";
import { EntityPermissions, PermissionsUser } from "types";

/**
 * Type definition for navigation sidebar links with permission-based visibility.
 * Supports nested/collapsible menu items and internationalization.
 */
export type NavLinkType = {
  type?: string;
  name: string;
  title?: string;
  id: string;
  icon?: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  collapse?: NavLinkType[];
  noCollapse?: boolean;
  route?: string;
  href?: string;
  lang?: string;
  requiredPermission?: string;
  moduleId?: string;
};

/**
 * Navigation sidebar configuration with role-based permission filtering.
 * 
 * Structure:
 * - type: 'collapse' for expandable menus, 'divider' for separators
 * - name: Display name (fallback if lang key missing)
 * - lang: i18n translation key for multi-language support
 * - icon: Material-UI icon component
 * - route: Path for navigation
 * - requiredPermission: Permission string required to view this item
 * - collapse: Nested child menu items
 * 
 * Menu Groups:
 * 1. Dashboards: Home, Appointments
 * 2. Billing: Accounting, Billing Encounters, Pricing, Payers, Claims
 * 3. Patients: New Patient, Patient List
 * 4. Employees: Doctors, Nurses
 * 5. Users: New User, User Management
 * 6. Clinical: Progress Notes, Templates, Office Visits
 * 7. Resources: Pharmacy, Facility, Resources, Insurance
 * 
 * Permissions are checked against user's role permissions from Redux auth state.
 * Items without required permissions are automatically filtered out by App.tsx.
 * 
 * @constant navRoutes
 */
const navRoutes: NavLinkType[] = [
  {
    type: "collapse",
    name: "Dashboards",
    title: "navRoutes.dashboardsExplanation",
    lang: "navRoutes.dashboards",
    id: "dashboards",
    icon: <DashboardRoundedIcon />,
    collapse: [
      {
        noCollapse: true,
        name: "Home",
        lang: "navRoutes.home",
        title: "navRoutes.homeExplanation",
        id: "dashboard",
        icon: <HomeRoundedIcon />,
        route: "/dashboard",
        requiredPermission: PermissionsUser[EntityPermissions.DASHBOARD].VIEW,
        moduleId: EntityPermissions.DASHBOARD,
      },
      {
        noCollapse: true,
        name: "Appointments",
        lang: "navRoutes.appointments",
        title: "navRoutes.appointmentsExplanation",
        id: "appointments",
        icon: <CalendarMonthRoundedIcon />,
        route: "/appointments",
        requiredPermission: PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,
        moduleId: EntityPermissions.APPOINTMENTS,
      },
    ],
  },
  {
    type: "divider",
    name: "divider",
    id: "divider",
    requiredPermission: PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
    moduleId: EntityPermissions.BILLING_ENCOUNTER,
  },
  {
    type: "collapse",
    name: "Billing",
    title: "navRoutes.billingExplanation",
    lang: "navRoutes.billing",
    id: "billing",
    icon: <PaidRoundedIcon />,
    collapse: [
      {
        noCollapse: true,
        name: "Accounting",
        lang: "navRoutes.Accountings",
        title: "navRoutes.AccountingsExplanation",
        id: "Accounting",
        icon: <PermContactCalendarRoundedIcon />,
        route: "/Accounting",
        requiredPermission: PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
        moduleId: EntityPermissions.BILLING_ENCOUNTER,
      },
      {
        noCollapse: true,
        name: "Billing Encounter",
        lang: "navRoutes.billingEncounter",
        title: "navRoutes.billingEncounterExplanation",
        id: "billing-encounter",
        icon: <ReceiptLongRoundedIcon />,
        route: "/billing-encounter",
        requiredPermission: PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
        moduleId: EntityPermissions.BILLING_ENCOUNTER,
      },
      {
        noCollapse: true,
        name: "Pricing",
        lang: "navRoutes.pricing",
        title: "navRoutes.pricingExplanation",
        id: "pricing",
        icon: <AttachMoneyRoundedIcon />,
        route: "/pricing",
        requiredPermission: PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
        moduleId: EntityPermissions.PRICING,
      },
      {
        noCollapse: true,
        name: "Payer",
        lang: "navRoutes.payers",
        title: "navRoutes.payersExplanation",
        id: "payer",
        icon: <ContactsRoundedIcon />,
        route: "/payer",
        requiredPermission: PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
        moduleId: EntityPermissions.PAYER,
      },
      {
        noCollapse: true,
        name: "Claim",
        lang: "navRoutes.claim",
        title: "navRoutes.claimExplanation",
        id: "claim",
        icon: <CurrencyExchange />,
        route: "/claim",
        requiredPermission: PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
        moduleId: EntityPermissions.CLAIM,
      },
    ],
  },
  {
    type: "divider",
    name: "divider1",
    id: "divider-1",
    requiredPermission: PermissionsUser[EntityPermissions.PATIENTS].VIEW,
    moduleId: EntityPermissions.PATIENTS,
  },
  {
    type: "collapse",
    name: "Patients",
    title: "navRoutes.patientsExplanation",
    lang: "navRoutes.patient",
    id: "patients",
    icon: <PersonRoundedIcon />,
    collapse: [
      {
        name: "New Patient",
        lang: "navRoutes.newPatient",
        title: "navRoutes.newPatientExplanation",
        id: "new-patient",
        icon: <PersonAddAltRoundedIcon />,
        route: "/new-patient",
        requiredPermission: PermissionsUser[EntityPermissions.NEW_PATIENTS].VIEW,
        moduleId: EntityPermissions.NEW_PATIENTS,
      },
      {
        name: "Patients",
        lang: "navRoutes.patients",
        title: "navRoutes.patientsExplanation",
        id: "patients",
        icon: <GroupsRoundedIcon />,
        route: "/patients",
        requiredPermission: PermissionsUser[EntityPermissions.PATIENTS].VIEW,
        moduleId: EntityPermissions.PATIENTS,
      },
    ],
  },
  {
    type: "divider",
    name: "divider2",
    id: "divider-2",
    requiredPermission: PermissionsUser[EntityPermissions.DOCTORS].VIEW,
    moduleId: EntityPermissions.DOCTORS,
  },
  {
    type: "collapse",
    name: "Employees",
    title: "navRoutes.employeesExplanation",
    lang: "navRoutes.employees",
    id: "employees",
    icon: <BadgeRoundedIcon />,
    collapse: [
      {
        name: "Doctors",
        lang: "navRoutes.doctors",
        title: "navRoutes.doctorsExplanation",
        id: "doctors",
        icon: <ContactEmergencyRoundedIcon />,
        route: "/doctors",
        requiredPermission: PermissionsUser[EntityPermissions.DOCTORS].VIEW,
        moduleId: EntityPermissions.DOCTORS,
      },
      {
        name: "Nurses",
        lang: "navRoutes.nurses",
        title: "navRoutes.nursesExplanation",
        id: "nurses",
        icon: <ContactEmergencyRoundedIcon />,
        route: "/nurses",
        requiredPermission: PermissionsUser[EntityPermissions.NURSE].VIEW,
        moduleId: EntityPermissions.NURSE,
      },
    ],
  },
  {
    type: "divider",
    name: "divider5",
    id: "divider-5",
    requiredPermission: PermissionsUser[EntityPermissions.NEW_USER].VIEW,
    moduleId: EntityPermissions.NEW_USER,
  },
  {
    type: "collapse",
    name: "User",
    title: "navRoutes.userExplanation",
    lang: "navRoutes.user",
    id: "userMenu",
    icon: <AccountCircleRoundedIcon />,
    collapse: [
      {
        name: "New User",
        lang: "navRoutes.newUser",
        title: "navRoutes.newUserExplanation",
        id: "new-user",
        icon: <GroupAddRoundedIcon />,
        route: "/new-user",
        requiredPermission: PermissionsUser[EntityPermissions.NEW_USER].VIEW,
        moduleId: EntityPermissions.NEW_USER,
      },
      {
        name: "Users",
        lang: "navRoutes.users",
        title: "navRoutes.usersExplanation",
        id: "users",
        icon: <GroupsRoundedIcon />,
        route: "/user",
        requiredPermission: PermissionsUser[EntityPermissions.USERS].VIEW,
        moduleId: EntityPermissions.USERS,
      },
    ],
  },
  {
    type: "divider",
    name: "divider6",
    id: "divider-6",
    requiredPermission: PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,
    moduleId: EntityPermissions.PROGRESS_NOTE,
  },
  {
    noCollapse: true,
    name: "Progress Note",
    title: "navRoutes.progressNoteExplanation",
    lang: "navRoutes.progressNote",
    id: "new-progress-note",
    icon: <EditNoteRoundedIcon />,
    route: "/progress-note",
    requiredPermission: PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,
    moduleId: EntityPermissions.PROGRESS_NOTE,
  },
  {
    type: "divider",
    name: "divider7",
    id: "divider-7",
    requiredPermission: PermissionsUser[EntityPermissions.HPI_PAGE].VIEW,
    moduleId: EntityPermissions.HPI_PAGE,
  },
  {
    noCollapse: true,
    name: "Templates",
    lang: "navRoutes.templates",
    title: "navRoutes.templateExplanation",
    id: "new-hpis-page",
    icon: <DocumentScannerRoundedIcon />,
    route: "/templates",
    requiredPermission: PermissionsUser[EntityPermissions.HPI_PAGE].VIEW,
    moduleId: EntityPermissions.HPI_PAGE,
  },
  {
    type: "divider",
    name: "divider8",
    id: "divider-8",
    requiredPermission: PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW,
    moduleId: EntityPermissions.OFFICE_VISIT,
  },
  {
    noCollapse: true,
    name: "Office Visit Page",
    title: "navRoutes.officeVisitExplanation",
    lang: "navRoutes.officeVisit",
    id: "new-office-visit-page",
    icon: <DocumentScannerRoundedIcon />,
    route: "/office-visit",
    requiredPermission: PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW,
    moduleId: EntityPermissions.OFFICE_VISIT,
  },
  {
    type: "divider",
    name: "divider9",
    id: "divider-9",
    requiredPermission: PermissionsUser[EntityPermissions.PHARMACY].VIEW,
    moduleId: EntityPermissions.PHARMACY,
  },
  {
    noCollapse: true,
    name: "Pharmacy Page",
    title: "navRoutes.pharmacyExplanation",
    lang: "navRoutes.pharmacy",
    id: "new-pharmacy-page",
    icon: <MedicationIcon />,
    route: "/pharmacy",
    requiredPermission: PermissionsUser[EntityPermissions.PHARMACY].VIEW,
    moduleId: EntityPermissions.PHARMACY,
  },
  {
    type: "divider",
    name: "divider10",
    id: "divider-10",
    requiredPermission: PermissionsUser[EntityPermissions.FACILITY].VIEW,
  },
  {
    noCollapse: true,
    name: "Facility Page",
    lang: "navRoutes.facility",
    title: "navRoutes.facilityExplanation",
    id: "new-facility-page",
    icon: <BloodtypeIcon />,
    route: "/facility",
    requiredPermission: PermissionsUser[EntityPermissions.FACILITY].VIEW,
    moduleId: EntityPermissions.FACILITY,
  },
  {
    type: "divider",
    name: "divider11",
    id: "divider-11",
    requiredPermission: PermissionsUser[EntityPermissions.RESOURCE].VIEW,
  },
  {
    noCollapse: true,
    name: "Resource Page",
    title: "navRoutes.resourceExplanation",
    lang: "navRoutes.resource",
    id: "new-resource-page",
    route: "/resource",
    requiredPermission: PermissionsUser[EntityPermissions.RESOURCE].VIEW,
    icon: <FileOpenIcon />,
    moduleId: EntityPermissions.RESOURCE,
  },
  {
    type: "divider",
    name: "divider12",
    id: "divider-12",
    requiredPermission: PermissionsUser[EntityPermissions.INSURANCE].VIEW,
  },
  {
    noCollapse: true,
    name: "Insurance Page",
    title: "navRoutes.insuranceExplanation",
    lang: "navRoutes.insurance",
    id: "new-insurance-page",
    route: "/insurance",
    requiredPermission: PermissionsUser[EntityPermissions.INSURANCE].VIEW,
    icon: <FileOpenIcon />,
    moduleId: EntityPermissions.INSURANCE,
  },
];

/**
 * Filters navigation routes based on disabled modules from environment variables and GeneralSettings.
 * Reads REACT_APP_DISABLED_MODULES (comma-separated module IDs) and removes matching routes.
 * Also filters based on isInsuranceModuleEnabled and isPharmacyModuleEnabled from GeneralSettings.
 * Converts environment variable values (UPPER_SNAKE_CASE) to EntityPermissions values (camelCase).
 * 
 * @param routes - Original navigation routes array
 * @param generalSettings - Optional general settings to determine module availability
 * @returns Filtered routes with disabled modules removed
 * 
 * Example .env configuration:
 * REACT_APP_DISABLED_MODULES=BILLING_ENCOUNTER,OFFICE_VISIT,PHARMACY,INSURANCE
 * 
 * Available Module IDs (case-insensitive, accepts snake_case or camelCase):
 * - DASHBOARD or dashboard: Home dashboard
 * - APPOINTMENTS or appointments: Appointment calendar
 * - BILLING_ENCOUNTER or billingEncounter: Billing encounters, Pricing, Payer, and Claims
 * - NEW_PATIENTS or newPatients: New patient creation
 * - PATIENTS or patients: Patient list/management
 * - DOCTORS or doctors: Doctor management
 * - NURSE or nurse: Nurse management
 * - NEW_USER or newUser: New user creation
 * - USERS or users: User management
 * - PROGRESS_NOTE or progressNote: Progress notes
 * - HPI_PAGE or hpisPage: Template management
 * - OFFICE_VISIT or officeVisit: Office visit management
 * - PHARMACY or pharmacy: Pharmacy module
 * - FACILITY or facility: Facility management
 * - RESOURCE or resource: Resource management
 * - INSURANCE or insurance: Insurance management
 */
export const filterDisabledModules = (
  routes: NavLinkType[],
  generalSettings?: { isInsuranceModuleEnabled?: boolean; isPharmacyModuleEnabled?: boolean; isOfficeVisitModuleEnabled?: boolean }
): NavLinkType[] => {
  const disabledModulesEnv = process.env.REACT_APP_HIDDEN_MODULES || '';

  const disabledModulesFromEnv = disabledModulesEnv
    .split(',')
    .map(module => module.trim())
    .filter(Boolean);

  // Start with disabled modules from environment
  const disabledModules: string[] = [];

  // Add modules disabled from GeneralSettings
  if (generalSettings) {
    if (generalSettings.isInsuranceModuleEnabled === false) {
      disabledModules.push(EntityPermissions.INSURANCE);
      // CLAIM module also depends on insurance module being enabled
      disabledModules.push(EntityPermissions.CLAIM);
    }
    if (generalSettings.isPharmacyModuleEnabled === false) {
      disabledModules.push(EntityPermissions.PHARMACY);
    }
    if (generalSettings.isOfficeVisitModuleEnabled === false) {
      disabledModules.push(EntityPermissions.OFFICE_VISIT);
    }
  }

  // Convert environment values to EntityPermissions values
  // Handles both UPPER_SNAKE_CASE and camelCase inputs
  const envDisabledModules = disabledModulesFromEnv.map(envValue => {
    // First try direct match (case-insensitive)
    const directMatch = Object.values(EntityPermissions).find(
      enumValue => enumValue.toLowerCase() === envValue.toLowerCase()
    );
    if (directMatch) return directMatch;

    // Convert UPPER_SNAKE_CASE to camelCase
    const camelCaseValue = envValue
      .toLowerCase()
      .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    // Find matching EntityPermissions value
    const match = Object.values(EntityPermissions).find(
      enumValue => enumValue.toLowerCase() === camelCaseValue.toLowerCase()
    );

    return match || envValue; // Fallback to original if no match found
  });

  // Combine environment and settings-based disabled modules
  disabledModules.push(...envDisabledModules);

  // If no modules are disabled, return original routes
  if (disabledModules.length === 0) return routes;

  return routes
    .map(route => {
      // Filter out routes with disabled moduleId
      if (route.moduleId && disabledModules.includes(route.moduleId)) {
        return null;
      }

      // Recursively filter collapse items
      if (route.collapse) {
        const filteredCollapse = route.collapse.filter(
          child => !child.moduleId || !disabledModules.includes(child.moduleId)
        );

        // If collapse becomes empty, remove the parent item
        if (filteredCollapse.length === 0) {
          return null;
        }

        return { ...route, collapse: filteredCollapse };
      }

      return route;
    })
    .filter(Boolean) as NavLinkType[];
};

export default navRoutes;

